import React, { useEffect, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { onAuthStateChanged, User } from "firebase/auth";
import { auth, firebaseEnv } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import { useSurveyRole, Role } from "./hooks/useSurveyRole";
import { Button } from "@mui/material";
import { db } from "./firebase";

import TopBar from "./components/TopBar";
import Login from "./pages/Login";
import SurveysList from "./pages/SurveysList";
import SurveyDetail from "./pages/SurveyDetail";
import SurveyEdit from "./pages/SurveyEdit";
import Viewer from "./pages/Viewer";
import Analytics from "./pages/Analytics";
import AnalyticsAlt from "./pages/AnalyticsAlt";
import Dashboard from "./pages/Dashboard";
import ListsList from "./pages/ListsList";
import ListDetail from "./pages/ListDetail";
import ListEdit from "./pages/ListEdit";
import DeletedItems from "./pages/DeletedItems";
import Changelog from "./pages/Changelog";
import Help from "./pages/Help";
import Account from "./pages/Account";
import AdminUsers from "./pages/AdminUsers";
import AdminSurveys from "./pages/AdminSurveys";
import AdminTools from "./pages/AdminTools";
import AdminRules from "./pages/AdminRules";
import AdminTestData from "./pages/AdminTestData";
import StakeholderRename from "./pages/StakeholderRename";
import NetworkSetup from "./pages/NetworkSetup";
import NetworkGraph from "./pages/NetworkGraph";
import NetworkDebug from "./pages/NetworkDebug";
import AnalyticsDebug from "./pages/AnalyticsDebug";
import ForgotPassword from "./pages/ForgotPassword";
import NetworkDetails from "./pages/NetworkDetails";
import MendelowMatrix from "./pages/MendelowMatrix";
import DiagSurveyShapes from "./pages/DiagSurveyShapes";
import { ConfettiEasterEgg } from "./components/ConfettiEasterEgg";

import "./surveyjs-license";
import { PageLayout } from "./components/PageLayout";
import { Serializer } from "survey-core";

// Extend dropdown with optional dataListId for list binding
Serializer.addProperty("dropdown", {
  name: "dataListId:string",
  category: "data",
  visibleIndex: 200,
  default: "",
});

// ---- Protected Route Wrapper ----
interface ProtectedRouteProps {
  element: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ element }) => {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <p>Loading…</p>
      </div>
    );
  }

  if (!user || user.isAnonymous) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return element;
};

interface AdminRouteProps {
  element: React.ReactElement;
}

const AdminRoute: React.FC<AdminRouteProps> = ({ element }) => {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (!u || u.isAnonymous) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        const role = snap.exists() ? (snap.data() as any)?.accountRole : undefined;
        setIsAdmin(role === "admin");
      } catch {
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <p>Loading…</p>
      </div>
    );
  }

  if (!user || user.isAnonymous) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/surveys" replace />;
  }

  return element;
};

// ---- Role-protected Route Wrapper (requires survey access) ----
interface RoleProtectedRouteProps {
  element: React.ReactElement;
  allowedRoles?: Role[];
}

const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({ element, allowedRoles = ["owner", "admin", "editor", "viewer"] }) => {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [authLoading, setAuthLoading] = useState(true);
  const location = useLocation();
  const { id: surveyId } = useParams<{ id: string }>();
  const { role, loading: roleLoading } = useSurveyRole(surveyId);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  if (authLoading || roleLoading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <p>Loading…</p>
      </div>
    );
  }

  if (!user || user.isAnonymous) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!surveyId || !role || !allowedRoles.includes(role)) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <p>You do not have access to this survey.</p>
        <p style={{ marginTop: 12 }}>
          <Button variant="outlined" onClick={() => (window.location.href = "/surveys")}>
            Go to Surveys
          </Button>
        </p>
      </div>
    );
  }

  return element;
};

interface EnvironmentBannerProps {
  userEmail?: string | null;
}

const EnvironmentBanner: React.FC<EnvironmentBannerProps> = ({ userEmail }) => {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isLocal = host === "localhost" || host === "127.0.0.1";
  const isStagingHost = host.includes("staging");
  const isStaging = firebaseEnv === "staging";

  // Show banner locally or on the staging hosting site
  if (!isLocal && !isStagingHost) return null;

  const label = isStaging ? "Staging database" : "Production database";
  const color = isStagingHost && !isLocal ? "#16a34a" : isStaging ? "#f97316" : "#dc2626";

  const toggleEnv = () => {
    if (!isLocal) return;
    const next = isStaging ? "prod" : "staging";
    localStorage.setItem("firebaseEnv", next);
    window.location.reload();
  };
  const shared: React.CSSProperties = {
    position: "fixed",
    left: 0,
    right: 0,
    padding: "6px 12px",
    textAlign: "center",
    fontWeight: 700,
    fontSize: 13,
    color: "#fff",
    background: color,
    zIndex: 9999,
    opacity: 0.9,
    pointerEvents: "none",
    letterSpacing: 0.5,
  };

  return (
    <>
      <div
        style={{
          ...shared,
          top: 0,
          pointerEvents: "none",
          display: "flex",
          justifyContent: "center",
          gap: 16,
          alignItems: "center",
        }}
      >
        <span>{label}</span>
        {userEmail && (
          <span style={{ fontWeight: 600, fontSize: 12, opacity: 0.9 }}>
            {userEmail}
          </span>
        )}
      </div>
      <div
        style={{
          ...shared,
          bottom: 0,
          display: "flex",
          justifyContent: "center",
          gap: 12,
          alignItems: "center",
          pointerEvents: "auto",
        }}
      >
        <span>{label}</span>
        {userEmail && (
          <span style={{ fontWeight: 600, fontSize: 12, opacity: 0.9 }}>
            {userEmail}
          </span>
        )}
        {isLocal && (
          <button
            onClick={toggleEnv}
            style={{
              pointerEvents: "auto",
              border: "1px solid rgba(255,255,255,0.6)",
              borderRadius: 4,
              padding: "4px 10px",
              background: "rgba(255,255,255,0.2)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Switch to {isStaging ? "Production" : "Staging"}
          </button>
        )}
      </div>
    </>
  );
};

export default function App() {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const isAuthenticated = !!user && !user.isAnonymous;

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <p>Loading…</p>
      </div>
    );
  }

  // --- Public viewer route (no TopBar) ---
  if (location.pathname.startsWith("/view/")) {
    return (
      <PageLayout>
        <Routes>
          <Route path="/view/:id" element={<Viewer />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </PageLayout>
    );
  }

  // --- Main app layout ---
  return (
    <>
      <ConfettiEasterEgg />
      <EnvironmentBanner userEmail={user?.email ?? null} />
      {isAuthenticated && <TopBar />}

      <PageLayout>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Login />} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/view/:id" element={<Viewer />} />

          {/* Root redirect — guarded to avoid redirect loop during auth load */}
          <Route
            path="/"
            element={
              loading ? (
                <div style={{ padding: 40, textAlign: "center" }}>
                  <p>Loading…</p>
                </div>
              ) : isAuthenticated ? (
                <Navigate to="/surveys" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />


          {/* Protected routes */}
          <Route
            path="/surveys"
            element={<ProtectedRoute element={<SurveysList />} />}
          />
          <Route
            path="/surveys/:id"
            element={<ProtectedRoute element={<SurveyDetail />} />}
          />
          <Route
            path="/surveys/:id/edit"
            element={<ProtectedRoute element={<SurveyEdit />} />}
          />
          <Route
            path="/surveys/:id/stakeholders"
            element={<ProtectedRoute element={<StakeholderRename />} />}
          />
          <Route
            path="/analytics/:id"
            element={<ProtectedRoute element={<Analytics />} />}
          />
          <Route
            path="/analytics/:id/debug"
            element={<ProtectedRoute element={<AnalyticsDebug />} />}
          />
          <Route
            path="/analyticsalt/:id"
            element={<ProtectedRoute element={<AnalyticsAlt />} />}
          />
          <Route
            path="/dashboard/:id"
            element={<ProtectedRoute element={<Dashboard />} />}
          />
          <Route
            path="/lists"
            element={<ProtectedRoute element={<ListsList />} />}
          />
          <Route
            path="/lists/:id"
            element={<ProtectedRoute element={<ListDetail />} />}
          />
          <Route
            path="/lists/:id/edit"
            element={<ProtectedRoute element={<ListEdit />} />}
          />
          <Route
            path="/deleted"
            element={<ProtectedRoute element={<DeletedItems />} />}
          />
          <Route
            path="/changelog"
            element={<ProtectedRoute element={<Changelog />} />}
          />
          <Route
            path="/help"
            element={<ProtectedRoute element={<Help />} />}
          />
          <Route
            path="/network/:id"
            element={<RoleProtectedRoute element={<NetworkSetup />} />}
          />
          <Route
            path="/network/:id/:source/:target"
            element={<RoleProtectedRoute element={<NetworkGraph />} />}
          />
          <Route
            path="/network/:id/:source/:target/details"
            element={<RoleProtectedRoute element={<NetworkDetails />} />}
          />
          <Route
            path="/network/:id/:source/:target/mendelow"
            element={<RoleProtectedRoute element={<MendelowMatrix />} />}
          />
          <Route
            path="/network/:id/:source/:target/debug"
            element={<RoleProtectedRoute element={<NetworkDebug />} />}
          />
          <Route
            path="/account"
            element={<ProtectedRoute element={<Account />} />}
          />
          <Route
            path="/diag"
            element={<Navigate to="/admin/diagnostics/surveys" replace />}
          />
          <Route
            path="/admin"
            element={<AdminRoute element={<AdminTools />} />}
          />
          <Route
            path="/admin/diagnostics/surveys"
            element={<AdminRoute element={<DiagSurveyShapes />} />}
          />
          <Route
            path="/admin/rules"
            element={<AdminRoute element={<AdminRules />} />}
          />
          <Route
            path="/admin/users"
            element={<AdminRoute element={<AdminUsers />} />}
          />
          <Route
            path="/admin/surveys"
            element={<AdminRoute element={<AdminSurveys />} />}
          />
          <Route
            path="/admin/test-data"
            element={<AdminRoute element={<AdminTestData />} />}
          />

          {/* Convenience redirect */}
          <Route path="/home" element={<Navigate to="/surveys" replace />} />

          {/* Catch-all */}
          <Route
            path="*"
            element={
              isAuthenticated ? (
                <Navigate to="/surveys" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </PageLayout>
    </>
  );
}
